import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/**
 * A name edited where it is shown: the group's in the room, a material's title on its page.
 *
 * One component for both, because the two differ only in who may and how long. Who may is
 * the caller's to decide — `canRename` — and the Worker decides again; this hides a button,
 * it does not grant anything. Enter saves and Escape cancels, as in any one-line field.
 *
 * `onSave` answers null when the name is saved, or the sentence to show when it was not.
 * The field stays open on a refusal, so what the teacher typed is not lost.
 */
export interface InlineRenameProps {
  /** What the teacher sees when not renaming — the heading. */
  children: ReactNode;
  canRename: boolean;
  value: string;
  /** "group" or "material", for the button's accessible name: "Rename group". */
  noun: string;
  /** The field's label: "Group name", "Title". */
  label: string;
  maxLength: number;
  onSave: (next: string) => Promise<string | null>;
}

export default function InlineRename({
  children, canRename, value, noun, label, maxLength, onSave,
}: InlineRenameProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fieldId = useId();
  const input = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  /** Focus goes back to the Rename button once the field closes, not to the top of the page. */
  const returnFocus = useRef(false);

  useEffect(() => {
    if (editing) input.current?.select();
    else if (returnFocus.current) { returnFocus.current = false; button.current?.focus(); }
  }, [editing]);

  const close = () => { returnFocus.current = true; setEditing(false); setError(null); };

  if (!canRename) return <>{children}</>;

  if (!editing) {
    return (
      <div className="inline-rename">
        {children}
        <button
          ref={button}
          type="button"
          className="secondary inline-rename-open"
          aria-label={`Rename ${noun}`}
          onClick={() => { setDraft(value); setError(null); setEditing(true); }}
        >
          Rename
        </button>
      </div>
    );
  }

  async function save() {
    const next = draft.trim();
    if (next === value) { close(); return; }
    if (next.length === 0) { setError(`Enter a ${label.toLowerCase()}.`); return; }
    setBusy(true);
    const refusal = await onSave(next);
    setBusy(false);
    if (refusal === null) close();
    else setError(refusal);
  }

  return (
    <form
      className="inline-rename inline-rename-form"
      aria-label={`Rename ${noun}`}
      onSubmit={(event) => { event.preventDefault(); void save(); }}
    >
      <label htmlFor={fieldId}>{label}</label>
      <input
        ref={input}
        id={fieldId}
        type="text"
        value={draft}
        maxLength={maxLength}
        disabled={busy}
        aria-invalid={error !== null}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); close(); } }}
      />
      <div className="row-actions">
        <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="secondary" disabled={busy} onClick={close}>Cancel</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </form>
  );
}
